import { useEffect, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { CompanyTitledPage } from "../../TitledPage";
import { CompanyPartHeader } from "../../../components/PartHeader";

import ShopGroupEditArea from "../ShopGroupEditArea";

import { SAMPLE_CUSTOMER_LIST, SAMPLE_CUSTOMER_SHOP_LIST, SAMPLE_CUSTOMER_SHOP_GROUP_LIST } from "../../../common/SampleData";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import Dialog from "@mui/material/Dialog";

import SquareIcon from '@mui/icons-material/Square';

import { blue, red, yellow } from "@mui/material/colors";

import { SYSTEM_PATH_COMPANY } from "../../../App";

const groupToggleStrings = ["order", "shipping"];
const groupToggleDisplayStrings = ["注文", "出荷"];
const shopGroupIdName = ["order_group_id", "shipping_group_id"];

const getCustomerLocationState = (customerId) => ({
  customerId: customerId
});

// 表示のまとめ方を切り替えるボタン
function GroupToggleButtons(props) {

  const onChangeToggle = (event, value) => {
    if (value != null) {
      props.setGroupToggle(value)
    }    
  }

  return (
    <ToggleButtonGroup
      value={props.groupToggle}
      exclusive
      onChange={onChangeToggle}
    >
      <ToggleButton value={0} sx={{fontSize: 15}}>
        注文グループ
      </ToggleButton>
      <ToggleButton value={1} sx={{fontSize: 15}}>
        出荷グループ
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

// 店舗追加ダイアログ
function AddGroupDialog({open, onClose, addGroup, groupToggle}) {

  const groupToggleDisplay = groupToggleDisplayStrings[groupToggle];

  const [groupName, setGroupName] = useState("");

  useEffect(() => {
    setGroupName("")
  }, [open])

  const onFocusInput = (event) => { event.target.select() }

  const onChangeGroupName = (event) => {
    setGroupName(event.target.value)
  }

  const onClickAddGroup = (event) => {
    addGroup(groupName, groupToggle)
    onClose(event)
  }

  const validateGroupName = () => groupName.length > 0

  return (
    <Dialog open={open} onClose={onClose}>
      <Box padding={2}>
        <Typography fontSize={20} fontWeight={700}>{groupToggleDisplay}グループ追加</Typography>

        <Box marginTop={2} marginBottom={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs='auto'>
              <SquareIcon />
            </Grid>
            <Grid item xs='auto'>
              {groupToggleDisplay}グループ名
            </Grid>
            <Grid item xs>
              <OutlinedInput
                onFocus={onFocusInput}
                onChange={onChangeGroupName} 
                value={groupName}
              />
            </Grid>
          </Grid>
        </Box>
          
        <Grid container>
          <Grid item xs textAlign='center'>
            <Button
              variant="contained"
              onClick={onClickAddGroup}
              disabled={!validateGroupName()}>
            追加
            </Button>
          </Grid>
          <Grid item xs textAlign='center'>
            <Button variant="outlined" onClick={onClose}>戻る</Button>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  )
}

// 店舗削除ダイアログ
function DeleteGroupDialog({open, onClose, groupToggle, deleteGroup, selectedDeleteGroup}) {

  const {group_id: groupId, group_name: groupName, shops: shops} = selectedDeleteGroup;
  const groupToggleDisplay = groupToggleDisplayStrings[groupToggle];

  const shopsListString = shops.reduce((prev_str, s_data) => (prev_str + s_data.name + ", "), "").slice(0, -2);

  const onClickDeleteGroup = (event) => {
    deleteGroup(groupId, groupToggle);
    onClose(event);
  }

  return(
    <Dialog open={open} onClose={onClose}>
      <Box padding={2}>
        <Typography fontSize={20} fontWeight={700}>{groupToggleDisplay}グループ削除</Typography>
        <Box marginTop={2} marginBottom={1}>
          <Typography>以下のグループを削除します。</Typography>
          <Typography>このグループに設定された店舗は、{groupToggleDisplay}グループが未設定の状態に戻ります。</Typography>        
          <Box backgroundColor={groupToggle === 0 ? red[100] : blue[100]} marginY={1} padding={1}>
            <Typography fontSize={20}>{groupToggleDisplay}グループ名: {groupName}</Typography>
          </Box>
          <Box backgroundColor={yellow[100]} marginY={1} padding={1}>
            <Typography fontSize={20}>設定されている店舗:</Typography>
            <Typography fontSize={20}>
              {shopsListString}
            </Typography>
          </Box>
        </Box>

        <Grid container>
          <Grid item xs textAlign='center'>
            <Button
              variant="contained"
              color='error'
              onClick={onClickDeleteGroup}
            >
              削除
            </Button>
          </Grid>
          <Grid item xs textAlign='center'>
            <Button variant="outlined" onClick={onClose}>戻る</Button>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  )
}


// 店舗情報編集ページ
export default function ShopGroupEditPage() {
  
  const location = useLocation()
  const navigate = useNavigate()
  const customerId = location.state.customerId

  const customerData = SAMPLE_CUSTOMER_LIST.filter((data) => data.customer_id === customerId)[0];

  const [ groupToggle, setGroupToggle ] = useState(0)

  const [ shopDataInput, setShopDataInput] = useState(SAMPLE_CUSTOMER_SHOP_LIST.filter((data) => data.customer_id === customerId).map((data) => ({...data})));
  const [ shopGroupDataInput, setShopGroupDataInput ] = useState(SAMPLE_CUSTOMER_SHOP_GROUP_LIST.filter((data) => data.customer_id === customerId).map((data) => ({...data})));

  const [ openAddGroupDialog, setOpenAddGroupDialog ] = useState(false);

  const [ openDeleteGroupDialog, setOpenDeleteGroupDialog ] = useState(false);
  const [ selectedDeleteGroup, setSelectedDeleteGroup ] = useState({
    group_id: null,
    group_name: null,
    shops: []
  });

  const getGroupDataList = (groupClass) => {
    return shopGroupDataInput.filter((data) => data.group_class_id === groupClass && data.delete_flag === 0)
  }

  const getUngroupedShops = (groupClass) => {
    return shopDataInput.filter((shopData) => shopData[shopGroupIdName[groupClass]] == null && shopData.delete_flag === 0);
  }

  const getShopGroupedList = (groupClass) => {
    const groups = getGroupDataList(groupClass);

    return groups.map((groupData) => ({
      group_id: groupData.group_id,
      group_name: groupData.group_name,
      shops: shopDataInput.filter((shopData) => shopData[shopGroupIdName[groupClass]] === groupData.group_id && shopData.delete_flag === 0)
    }));
  }

  const changeGroup = (groupClass) => (shopId, groupId) => {    
    
    const shopIndex = shopDataInput.findIndex((data) => data.shop_id === shopId);

    const newShopDataInput = Array.from(shopDataInput);
    newShopDataInput[shopIndex][shopGroupIdName[groupClass]] = groupId;
    setShopDataInput(newShopDataInput);
  }

  const addGroup = (groupName, groupClass) => {
    const newGroupId = "new_" + (shopGroupDataInput.length + 1).toString().padStart(3, "0")
    setShopGroupDataInput([...shopGroupDataInput, {
      customer_id: customerId,
      group_id: newGroupId,
      group_name: groupName,
      group_class_id: groupClass,
      delete_flag: 0
    }])
  }

  const deleteGroup = (groupId, groupClass) => {
    const shops = Array.from(shopDataInput.filter((data) => data[groupToggleStrings[groupClass] + "_group_id"] === groupId))
    shops.forEach((s_data) => {
      changeGroup(groupClass)(s_data.shop_id, null);
    });

    const shopGroupDataIndex = shopGroupDataInput.findIndex((g_data) => g_data.customer_id === customerId && g_data.group_id === groupId);
    const newShopGroupDataInput = Array.from(shopGroupDataInput);
    newShopGroupDataInput[shopGroupDataIndex].delete_flag = 1;

    setShopGroupDataInput(newShopGroupDataInput);
  }
  
  const updateShopGroupInfoData = () => {

    shopGroupDataInput.forEach((g_data) => {
      var shopGroupDataIndex = SAMPLE_CUSTOMER_SHOP_GROUP_LIST.findIndex((data) => data.customer_id === customerId && data.group_id === g_data.group_id);
      if (shopGroupDataIndex >= 0) {
        SAMPLE_CUSTOMER_SHOP_GROUP_LIST[shopGroupDataIndex] = g_data;
      } else {
        const newGroupId = (SAMPLE_CUSTOMER_SHOP_GROUP_LIST.filter((data) => data.customer_id === customerId).length + 1).toString().padStart(3,"0");
        
        const groupClass = g_data.group_class_id;
        shopDataInput.forEach((s_data) => {
          if (s_data[shopGroupIdName[groupClass]] === g_data.group_id ) {
            s_data[shopGroupIdName[groupClass]] = newGroupId;
          }
        })

        g_data.group_id = newGroupId;
        SAMPLE_CUSTOMER_SHOP_GROUP_LIST.push(g_data);
      }
    })

    shopDataInput.forEach((s_data) => {
      var shopData = SAMPLE_CUSTOMER_SHOP_LIST.filter((data) => data.customer_id === customerId && data.shop_id === s_data.shop_id)[0]
      shopData.order_group_id = s_data.order_group_id;
      shopData.shipping_group_id = s_data.shipping_group_id;
    })
  }

  const moveCustomerInfo = () => {
    navigate(SYSTEM_PATH_COMPANY + "/customer_information", { state: getCustomerLocationState(customerId) })
  }

  const onClickReturnCustomerInfo = (event) => {
    moveCustomerInfo()
  }

  const onClickUpdate = (event) => {
    updateShopGroupInfoData()
    moveCustomerInfo()
  }

  const onClickAddGroup = (event) => {
    setOpenAddGroupDialog(true);
  }

  const onCloseAddGroupDialog = (event) => {
    setOpenAddGroupDialog(false)
  }

  const onClickDeleteGroup = (groupData) => (event) => {
    setSelectedDeleteGroup(groupData)
    setOpenDeleteGroupDialog(true)
  }

  const onCloseDeleteGroupDialog = (event) => {
    setOpenDeleteGroupDialog(false)
  }

  return (
    <CompanyTitledPage title="店舗グループ編集">
      <Typography>
        取引先ID : {customerId}
      </Typography>

      <Typography fontWeight="bold" fontSize={30}>
        {customerData.name}
      </Typography>

      <CompanyPartHeader>
        <Grid container spacing={1}>
          <Grid item xs>
            <Typography fontWeight="bold" fontSize={20}>店舗グループの編集</Typography>
          </Grid>
          <Grid item xs='auto'>
            <Button variant="contained" onClick={onClickUpdate}>更新</Button>
          </Grid> 
          <Grid item xs='auto'>
            <Button variant="outlined" onClick={onClickReturnCustomerInfo}>戻る</Button>
          </Grid>         
        </Grid>
      </CompanyPartHeader>

      <Box marginBottom={1}>
        <Grid container alignItems="center">
          <Grid item xs>
            <GroupToggleButtons groupToggle={groupToggle} setGroupToggle={setGroupToggle} />
          </Grid>
          <Grid item xs="auto">
            <Button variant="outlined" onClick={onClickAddGroup}>グループを追加</Button>
          </Grid>
        </Grid>
      </Box>

      <Box hidden={groupToggle!==0}>
        <ShopGroupEditArea
          ungroupedShops={getUngroupedShops(0)}
          shopGroupedList={getShopGroupedList(0)}
          changeGroup={changeGroup(0)}
          onClickDeleteGroup={onClickDeleteGroup}
        />
      </Box>

      <Box hidden={groupToggle!==1}>
        <ShopGroupEditArea
          ungroupedShops={getUngroupedShops(1)}
          shopGroupedList={getShopGroupedList(1)}
          changeGroup={changeGroup(1)}
          onClickDeleteGroup={onClickDeleteGroup}
        />
      </Box>

      <AddGroupDialog
        open={openAddGroupDialog}
        onClose={onCloseAddGroupDialog}
        groupToggle={groupToggle}
        addGroup={addGroup}
      />

      <DeleteGroupDialog
        open={openDeleteGroupDialog}
        onClose={onCloseDeleteGroupDialog}
        groupToggle={groupToggle}
        deleteGroup={deleteGroup}
        selectedDeleteGroup={selectedDeleteGroup}        
      />
      
    </CompanyTitledPage>
  );
}
